import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	IconButton,
	Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useFormsContext } from "../context/formsContext";
import type { ParametersNewForm } from "../models/ParametersNewForm";
import {
	approveFormInstance,
	submitFormInstance,
	unapproveFormInstance,
	upsertRadioButtonAnswer,
} from "../repositories/FormsRepository";
import { EditInstanceSubmitAndApprove } from "./EditInstanceSubmitAndApprove";
import { useGetSelectedInstanceStatus } from "../hooks";
import CloseIcon from "@mui/icons-material/Close";
import { ErrorModal, HelmetDexteel, useToken } from "@dexteel/mesf-core";
import { Questions } from "./Questions";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const EditInstance = ({ show, onHide, suffixTitle }: Props) => {
	const {
		state: {
			formInstanceModifiedRows,
			formInstanceRows,
			formComment,
			submitInitials,
			approveInitials,
			formInstanceStatus,
			formInstanceSections,
			formInstanceQuestions,
			layoutCode,
		},
		actions: { setFormInstanceModifiedRows },
	} = useFormsContext();

	const { hasPermission } = useToken();

	const [canApprove, setCanApprove] = useState<boolean>(
		hasPermission("FORMS.ApproveFormInstance" as any),
	);

	const getStatus = useGetSelectedInstanceStatus();

	const [error, setError] = useState("");
	const [open, setOpen] = useState(false);
	const [shouldUpdate, setShouldUpdate] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("xl");
	const [saveButtonEnabled, setSaveButtonEnabled] = useState<boolean>(true);

	const [savingData, setSavingData] = useState<boolean>(false);
	const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
	const [aprroveSuccess, setApproveSuccess] = useState<boolean>(false);
	const [unaprroveSuccess, setUnapproveSuccess] = useState<boolean>(false);

	const { handleSubmit, reset } = useForm<ParametersNewForm>();

	const handleCancel = () => {
		onHide(false);
	};

	const onSubmit: SubmitHandler<ParametersNewForm> = async () => {
		handleSaveOptions();
		onHide(false);
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
		setSubmitSuccess(false);
		setSubmitSuccess(false);
		setApproveSuccess(false);
		setUnapproveSuccess(false);
	};

	const handleonHide = () => {
		onHide(true);
	};

	const handleSaveOptions = async () => {
		setIsSubmitLoading(true);
		const jsonStr = JSON.stringify(formInstanceModifiedRows);

		if (formInstanceModifiedRows.length >= 1) {
			const res = await upsertRadioButtonAnswer(
				jsonStr,
				formComment,
				formInstanceRows[0].FormInstanceId,
			);
			if (res.ok) {
				setIsSubmitLoading(false);
				setOpen(false);
				setFormInstanceModifiedRows([]);
				getStatus();
				onHide(true);
			} else {
				setError(res.message);
				setIsSubmitLoading(false);
			}
		} else setIsSubmitLoading(false);
	};

	const handleSubmitClick = async () => {
		setSavingData(true);
		const res = await submitFormInstance(
			formInstanceRows[0].FormInstanceId,
			formComment,
			submitInitials,
		);
		if (res.ok) {
			handleSaveOptions();
			getStatus();
			setSavingData(false);
			setSubmitSuccess(true);
			onHide(true);
		} else {
			setError(res.message);
			handleSaveOptions();
			getStatus();
			setSavingData(false);
			setSubmitSuccess(false);
			onHide(false);
		}
	};

	const handleApproveClick = async () => {
		setSavingData(true);
		const res = await approveFormInstance(
			formInstanceRows[0].FormInstanceId,
			approveInitials,
		);
		if (res.ok) {
			getStatus();
			setShouldUpdate(true);
			setApproveSuccess(true);
			setSavingData(false);
			onHide(false);
		} else {
			getStatus();
			setApproveSuccess(false);
			setSavingData(false);
			onHide(true);
			setError(res.message);
		}
		if (formInstanceStatus === "S") {
			// Submitted
		} else if (formInstanceStatus === "A") {
			// Approved
		}
	};

	const handleUnaproveClick = async () => {
		const res = await unapproveFormInstance(formInstanceRows[0].FormInstanceId);
		if (res.ok) {
			getStatus();
			setUnapproveSuccess(true);
			setSavingData(false);
			setShouldUpdate(true);
		} else {
			setError(res.message);
			getStatus();
			setUnapproveSuccess(false);
			setSavingData(false);
		}
	};

	useEffect(() => {
		if (show) {
			reset();
			setShouldUpdate(false);
		} else {
			if (shouldUpdate) onHide(true);
		}
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Edit Instance${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Grid2 container>
				<Grid2>
					<Dialog
						fullWidth={fullWidth}
						maxWidth={maxWidth}
						open={show}
						onClose={() => handleCancel()}
					>
						<Typography
							variant="h6"
							style={{
								fontWeight: 600,
								margin: "25px 20px -15px 20px",
								textAlign: "center",
							}}
						>
							Form: {formInstanceRows[0]?.FormName || "Form Instance"} |{" "}
							{formInstanceRows[0]?.AssetName}
						</Typography>
						<IconButton
							aria-label="close"
							onClick={() => handleCancel()}
							style={{
								position: "absolute",
								right: 0,
								top: -5,
								color: "red",
							}}
						>
							<CloseIcon />
						</IconButton>
						<hr />
						<form onSubmit={handleSubmit(onSubmit)}>
							<DialogContent dividers>
								<Questions
									formInstanceSections={formInstanceSections}
									formInstanceQuestions={formInstanceQuestions}
									layoutCode={layoutCode}
								/>
								<EditInstanceSubmitAndApprove />
							</DialogContent>
							<DialogActions style={{ padding: "0", margin: "5px 0" }}>
								<Grid2 container spacing={2} style={{ margin: "0 15px" }}>
									{formInstanceStatus === "A" || formInstanceStatus == "S" ? (
										<Grid2
											style={{ margin: 0 }}
											justifyContent="flex-end"
											size={{ md: 3, xs: 12 }}
										>
											<Button
												disabled={savingData}
												variant="contained"
												onClick={handleUnaproveClick}
												color="secondary"
												fullWidth
												startIcon={
													savingData ? (
														<CircularProgress size={20} color="inherit" />
													) : null
												}
											>
												{savingData ? "Saving..." : "Edit"}
											</Button>
										</Grid2>
									) : (
										<Grid2
											style={{ margin: 0 }}
											size={{ md: 1, xs: 12 }}
										></Grid2>
									)}
									<Grid2 style={{ margin: 0 }} size={{ md: 7, xs: 12 }}></Grid2>
									{formInstanceStatus === "S" && canApprove && (
										<Grid2 style={{ margin: 0 }} size={{ md: 2, xs: 12 }}>
											<Button
												disabled={savingData || approveInitials === ""}
												variant="contained"
												onClick={handleApproveClick}
												color="primary"
												fullWidth
												startIcon={
													savingData ? (
														<CircularProgress size={20} color="inherit" />
													) : null
												}
											>
												{savingData ? "Saving..." : "Approve"}
											</Button>
										</Grid2>
									)}

									{formInstanceStatus == "O" && (
										<Grid2 style={{ margin: 0 }} size={{ md: 2, xs: 12 }}>
											<Button
												disabled={submitInitials === ""}
												variant="contained"
												onClick={handleSubmitClick}
												color="primary"
												fullWidth
											>
												{savingData ? "Saving..." : "Submit"}
											</Button>
										</Grid2>
									)}
									{formInstanceStatus == "O" && (
										<Grid2 style={{ margin: 0 }} size={{ md: 2, xs: 12 }}>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={isSubmitLoading || !saveButtonEnabled}
												variant="contained"
												color="primary"
												type="submit"
											>
												Save Changes
											</Button>
										</Grid2>
									)}
								</Grid2>
							</DialogActions>
						</form>
					</Dialog>
				</Grid2>
			</Grid2>
			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="success" onClose={handleClose}>
					Successfully saved!
				</Alert>
			</Snackbar>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
